/**
 * getInboundEligibleSchemas.ts
 *
 * Utility to find schema types that can be assigned TO a given schema via its perspectives.
 * This is the inverse of getEligibleSchemaObjects - it answers "what can be assigned to me?"
 * rather than "what can I be assigned to?".
 *
 * Used by Related Spheres factor configuration to show only relevant property filters
 * based on which schema types can actually be related to the current sphere.
 */

import { Schema } from 'types/schema';

export interface GetInboundEligibleSchemasOptions {
  /** The schema of the current sphere (the one receiving assignments) */
  currentSchema: Schema;
  /** All schemas in the system */
  allSchemas: Schema[];
  /** Optional: filter to specific perspectives (if not provided, uses all perspectives on currentSchema) */
  perspectiveSystemNames?: string[];
}

/**
 * Find schema types that can be assigned TO the given schema via its perspectives.
 *
 * For each perspective on the current schema, examines the assignee_filter rules
 * to determine which schema types are eligible to be assigned.
 *
 * @example
 * // Schema 'center_project' has perspective 'center_parent' with assignee_filter: [{ property: 'type', value: 'center_pillar' }]
 * const eligible = getInboundEligibleSchemas({
 *   currentSchema: projectSchema,
 *   allSchemas: allSchemas,
 * });
 * // Returns [pillarSchema] because only pillar types can be assigned to projects via center_parent
 *
 * @param options - Configuration options
 * @returns Array of schemas that can be assigned to the current schema
 */
export function getInboundEligibleSchemas({
  currentSchema,
  allSchemas,
  perspectiveSystemNames,
}: GetInboundEligibleSchemasOptions): Schema[] {
  const eligibleSystemNames = new Set<string>();

  // Get perspectives from current schema, excluding hidden ones
  const perspectives = (currentSchema.perspectives || []).filter(
    (p) => !p.is_hidden
  );

  // Filter to specific perspectives if provided
  const filteredPerspectives = perspectiveSystemNames?.length
    ? perspectives.filter((p) => perspectiveSystemNames.includes(p.system_name))
    : perspectives;

  // If no perspectives match, return empty array
  if (filteredPerspectives.length === 0) {
    return [];
  }

  for (const perspective of filteredPerspectives) {
    const filters = perspective.assignee_filter || [];

    if (filters.length === 0) {
      // No filter = all non-abstract, non-hidden schemas are eligible
      allSchemas
        .filter((s) => !s.is_abstract && !s.is_hidden)
        .forEach((s) => eligibleSystemNames.add(s.system_name));
    } else {
      // Apply assignee_filter rules
      for (const filter of filters) {
        if (filter.property === 'type') {
          // Direct type match
          eligibleSystemNames.add(filter.value);
        } else if (filter.property === 'label') {
          // Find all schemas with matching node_label
          allSchemas
            .filter(
              (s) =>
                s.node_label === filter.value && !s.is_abstract && !s.is_hidden
            )
            .forEach((s) => eligibleSystemNames.add(s.system_name));
        }
        // Extend with other property types if needed in the future
      }
    }
  }

  // Return the actual schema objects for eligible system names
  return allSchemas.filter((s) => eligibleSystemNames.has(s.system_name));
}
